import moment from "moment";
import { useEffect, useState } from "react";
import { Drawer, Input } from "antd";
import crossIcon from "../../../../assets/cross.svg";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import UploadModal from "../../../../modules/subjects-management/subject/uploads/upload-modal";
import ApiUserUploads from "../../../../api/user-uploads";
import formatAnswerValue from "../../../../common/utils/formatAnswerValue";
import TaskTypeChip from "../task-type-chip";
import { Editor } from "../../../../components/editor/editor";
import { useSignedHtml } from "../../../../components/editor/refreshSignedMedia";

// Exported so portal-style drawers (e.g. AssignedTaskDrawer) can render the
// same completion responses block without duplicating the logic.
export const Quiz = ({ items = [], onChange, isEdit }) => {
  const [images, setImages] = useState({});
  const [uploads, setUploads] = useState({});
  const [selectedUpload, setSelectedUpload] = useState(null);

  useEffect(() => {
    const fetchImages = async () => {
      try {
        const imageLinks = {};
        const imageUploads = {};

        for (const item of items) {
          if (item.type === "photoPicker" && item.text) {
            const response = await ApiSubjectsManagement.getSubjectUpload({
              uploadId: item.text,
            });
            imageLinks[item.text] = response.data.signedUrl;
            imageUploads[item.text] = response.data;
          } else if (item.userUploadId) {
            const response = await ApiUserUploads.getSignedUrls({
              size: "both",
              imageIds: [item.userUploadId],
            });

            const urls = response.data[item.userUploadId];
            if (urls) {
              imageLinks[item.userUploadId] = urls.thumb || urls.original;
              imageUploads[item.userUploadId] = urls;
            }
          }
        }

        setImages(imageLinks);
        setUploads(imageUploads);
      } catch (error) {
        console.error("Upload error:", error);
      }
    };

    fetchImages();
  }, [items]);

  const normalizeAnswerValue = ({ text, answer }) => {
    const rawValue = text ?? answer;

    if (
      rawValue &&
      typeof rawValue === "object" &&
      Array.isArray(rawValue.answers)
    ) {
      return rawValue.answers.map((item) => item.label).filter(Boolean);
    }

    return rawValue;
  };

  const view = items.map((item, index) => {
    const {
      text,
      answer,
      questionText,
      id,
      type,
      userUploadId,
      skipped,
    } = item;

    let formattedAnswer;

    if (skipped) {
      formattedAnswer = "Skipped";
    } else if (type === "photoPicker" && images[text]) {
      formattedAnswer = (
        <img
          src={images[text]}
          alt="Uploaded"
          className="photo-preview"
          onClick={() => setSelectedUpload(uploads[text])}
          style={{ cursor: "pointer" }}
        />
      );
    } else if (userUploadId && images[userUploadId]) {
      formattedAnswer = (
        <img
          src={images[userUploadId]}
          alt="Uploaded"
          className="photo-preview"
          onClick={() =>
            setSelectedUpload({ signedUrl: uploads[userUploadId].original })
          }
          style={{ cursor: "pointer" }}
        />
      );
    } else {
      const normalizedValue = normalizeAnswerValue({ text, answer });

      formattedAnswer = Array.isArray(normalizedValue) ? (
        <ul>
          {normalizedValue.map((itemLabel, answerIndex) => (
            <li key={answerIndex}>{formatAnswerValue(itemLabel)}</li>
          ))}
        </ul>
      ) : (
        formatAnswerValue(normalizedValue)
      );
    }

    return (
      <div key={id || index} className="survey__drawer__form-item">
        <p className="survey__drawer__form-item-label w250">
          {questionText || "N/A"}
        </p>
        <div>{formattedAnswer}</div>
      </div>
    );
  });

  const edit = items.map((item, index) => {
    const { text, answer, questionText, id } = item;
    const normalizedValue = normalizeAnswerValue({ text, answer });

    const formattedAnswer = Array.isArray(normalizedValue)
      ? normalizedValue.map(formatAnswerValue).join(", ")
      : formatAnswerValue(normalizedValue);

    return (
      <div key={id || index} className="survey__drawer__form-item">
        <p className="survey__drawer__form-item-label w250">
          {questionText || "N/A"}
        </p>
        <Input
          defaultValue={formattedAnswer}
          onChange={onChange}
          maxLength={1000}
        />
      </div>
    );
  });

  return (
    <>
      {isEdit ? edit : view}
      {selectedUpload && (
        <UploadModal
          image={selectedUpload}
          isOpen={!!selectedUpload}
          close={() => setSelectedUpload(null)}
        />
      )}
    </>
  );
};

const TaskDrawer = ({ isOpenDrawer, close, task }) => {
  const { response, completedAt, status, dueDate } = task || {};
  const { backgroundColor, avatar, avatarColor, title, description } =
    task?.DailyTask || {};
  // its images carry signed URLs that expire, so they are refreshed on open
  const signedDescription = useSignedHtml(description, isOpenDrawer);

  return (
    <Drawer
      styles={{ body: { padding: 32 } }}
      open={isOpenDrawer}
      size={680}
      closable={false}
      onClose={close}
    >
      <div className="task-drawer__header">
        {avatar ? (
          <div
            className="icon-wrapper"
            style={{
              backgroundColor,
            }}
          >
            <img src={encodeSvgWithColor(avatar, avatarColor)} alt="avatar" />
          </div>
        ) : (
          <div />
        )}

        <div className="task-drawer__header__right">
          <UserActivityStatus userStatus={status || "PENDING"} />
          <img src={crossIcon} alt="close" onClick={close} />
        </div>
      </div>

      <div>
        <p className="task-drawer__title">{title}</p>
        {description && (
          <div className="task-drawer__description">
            {/* rich text since LF360-1852 — plain descriptions render the same */}
            <Editor readonly content={signedDescription} />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <TaskTypeChip task={task} />
        </div>

        <div className="task-drawer__history-item-container">
          <p className="task-drawer__history-item__title">Due date</p>
          <p className="task-drawer__history-item__result">
            {moment(dueDate).utc().format("DD MMM YYYY, HH:mm")}, UTC
          </p>
        </div>

        {completedAt && (
          <div className="task-drawer__history-item-container">
            <div>
              <div className="border-bottom-1">
                <p className="task-drawer__history-item__title">Completed</p>
                <p className="task-drawer__history-item__result mb10">
                  {moment(completedAt).utc().format("DD MMM YYYY, HH:mm")}, UTC
                </p>
              </div>
            </div>
            <div>
              <Quiz items={response} />
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default TaskDrawer;
